/**
 * RulingSubmitModal: Director submits a ruling on a RULING-* task.
 *
 * Closes the memo → board → ruling → unblock loop. When the Director
 * acts on a RULING-* task on the board, this modal collects the
 * decision (approved/rejected/modified) + optional ruling_text and
 * posts to the backend's /api/v1/memos/ruling endpoint. The backend
 * then:
 *   1. Updates the memo file's `director_ruling` YAML field
 *   2. Marks the RULING-* task as status=done
 *   3. Strips `blocked_on:<question_id>` tags from downstream tasks
 *   4. Flips memo status to RATIFIED if all questions are resolved
 *
 * See BOARD-RULING-MODAL-1 dispatch.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Check, FileText, Loader2, X } from 'lucide-react';
import { memos } from '@/api/engine';
import type { MemoRulingDecision, MemoRulingResponse, TaskItem } from '@/api/types';


// Three-value enum matching the backend pattern ^(approved|rejected|modified)$
const DECISION_OPTIONS: Array<{
  value: MemoRulingDecision;
  label: string;
  description: string;
  color: string;
}> = [
  {
    value: 'approved',
    label: 'Approved',
    description: 'Accept the proposed outcome / recommendation as-is.',
    color: 'text-[#39FF14]',
  },
  {
    value: 'modified',
    label: 'Modified',
    description: 'Accept a variant — put the variant in the ruling text below.',
    color: 'text-yellow-300',
  },
  {
    value: 'rejected',
    label: 'Rejected',
    description: 'Decline the proposal entirely.',
    color: 'text-red-400',
  },
];

export interface RulingSubmitModalProps {
  open: boolean;
  task: TaskItem;
  memoPath: string;
  questionId: string;
  onClose: () => void;
  onSuccess: (response: MemoRulingResponse) => void;
}

export function RulingSubmitModal({
  open,
  task,
  memoPath,
  questionId,
  onClose,
  onSuccess,
}: RulingSubmitModalProps) {
  const [decision, setDecision] = useState<MemoRulingDecision | null>(null);
  const [rulingText, setRulingText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Reset on open/close so re-opening the modal doesn't show stale state.
  useEffect(() => {
    if (!open) return;
    setDecision(null);
    setRulingText('');
    setSubmitting(false);
    setError(null);
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!decision) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await memos.submitRuling({
        memo_path: memoPath,
        question_id: questionId,
        decision,
        ruling_text: rulingText.trim() || undefined,
      });
      onSuccess(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit ruling');
      setSubmitting(false);
    }
  }, [decision, memoPath, questionId, rulingText, onSuccess]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={submitting ? undefined : onClose}
        className="fixed inset-0 bg-black/50 z-[60]"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        data-testid="ruling-submit-modal"
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-[520px] max-w-[92vw] max-h-[85vh] overflow-hidden rounded-xl shadow-2xl"
        style={{
          backgroundColor: 'var(--theme-surface-secondary, #1a1f2a)',
          borderColor: 'var(--theme-surface-tertiary, #222836)',
          border: '1px solid var(--theme-surface-tertiary, #222836)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3.5 border-b"
          style={{ borderColor: 'var(--theme-surface-tertiary, #222836)' }}
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-cyan-400" />
            <h3
              className="text-sm font-semibold"
              style={{ color: 'var(--theme-text-primary, #f4f7fb)' }}
            >
              Submit Director Ruling
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-1 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <X className="w-4 h-4" style={{ color: 'var(--theme-text-muted, #8e98ab)' }} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto max-h-[70vh]">
          {/* Memo + question context */}
          <div
            className="rounded-lg p-3 space-y-1"
            style={{
              backgroundColor: 'var(--theme-surface-tertiary, #222836)',
            }}
          >
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider"
                 style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
              Ruling on
            </div>
            <div className="space-y-1">
              <div className="text-xs font-mono break-all"
                   style={{ color: 'var(--theme-text-secondary, #c2c9d6)' }}>
                <span className="text-cyan-300">memo:</span> {memoPath}
              </div>
              <div className="text-xs font-mono"
                   style={{ color: 'var(--theme-text-secondary, #c2c9d6)' }}>
                <span className="text-cyan-300">question:</span> {questionId}
              </div>
              {task.task_id && (
                <div className="text-xs font-mono"
                     style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>
                  <span>task:</span> {task.task_id}
                </div>
              )}
            </div>
          </div>

          {/* Decision picker */}
          <div className="space-y-2">
            <label
              className="text-[10px] font-mono uppercase tracking-wider block"
              style={{ color: 'var(--theme-text-muted, #8e98ab)' }}
            >
              Decision <span className="text-red-400">*</span>
            </label>
            <div className="space-y-1.5">
              {DECISION_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 rounded-lg p-3 border cursor-pointer transition-colors ${
                    decision === opt.value
                      ? 'bg-white/5'
                      : 'hover:bg-white/[0.02]'
                  }`}
                  style={{
                    borderColor:
                      decision === opt.value
                        ? 'var(--theme-success, #39FF14)'
                        : 'var(--theme-surface-tertiary, #222836)',
                  }}
                >
                  <input
                    type="radio"
                    name="decision"
                    value={opt.value}
                    checked={decision === opt.value}
                    onChange={() => setDecision(opt.value)}
                    disabled={submitting}
                    className="mt-0.5 accent-[#39FF14]"
                    data-testid={`ruling-decision-${opt.value}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-semibold ${opt.color}`}>
                      {opt.label}
                    </div>
                    <div
                      className="text-[11px] mt-0.5"
                      style={{ color: 'var(--theme-text-muted, #8e98ab)' }}
                    >
                      {opt.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Ruling text */}
          <div className="space-y-2">
            <label
              className="text-[10px] font-mono uppercase tracking-wider block"
              style={{ color: 'var(--theme-text-muted, #8e98ab)' }}
            >
              Ruling text <span style={{ color: 'var(--theme-text-muted, #8e98ab)' }}>(optional, but encouraged for Modified)</span>
            </label>
            <textarea
              ref={textareaRef}
              value={rulingText}
              onChange={(e) => setRulingText(e.target.value)}
              disabled={submitting}
              placeholder="Your reasoning — what you're approving, what variant if Modified, why if Rejected. Lands in the memo's director_ruling field verbatim."
              rows={5}
              data-testid="ruling-text-input"
              className="w-full px-3 py-2 text-xs font-mono rounded-lg resize-y disabled:opacity-50"
              style={{
                backgroundColor: 'var(--theme-surface-primary, #0f1218)',
                border: '1px solid var(--theme-surface-tertiary, #222836)',
                color: 'var(--theme-text-secondary, #c2c9d6)',
              }}
            />
          </div>

          {/* Error */}
          {error && (
            <div
              role="alert"
              data-testid="ruling-error"
              className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-[11px]"
              style={{
                backgroundColor: 'rgba(248, 113, 113, 0.1)',
                color: 'rgb(248, 113, 113)',
                border: '1px solid rgba(248, 113, 113, 0.3)',
              }}
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-5 py-3 border-t"
          style={{ borderColor: 'var(--theme-surface-tertiary, #222836)' }}
        >
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              color: 'var(--theme-text-secondary, #c2c9d6)',
              backgroundColor: 'var(--theme-surface-tertiary, #222836)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!decision || submitting}
            data-testid="ruling-submit-button"
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              color: '#0a0a0a',
              backgroundColor: 'var(--theme-success, #39FF14)',
            }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <Check className="w-3.5 h-3.5" />
                Submit Ruling
              </>
            )}
          </button>
        </div>
      </motion.div>
    </>
  );
}
